import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList as List } from 'react-window';
import cn from 'classnames';
import { enableMapSet, produce } from 'immer';
import { random } from 'lodash';

import { SpinnerOverlay } from '@components/common';

import { uuid } from '@utils/common';
import { isChildNode, isCommonNodeRoot } from '@utils/tree/commonTreeHelper';
import { createFlatTreePath } from '@utils/treeFormatter/treeFormatter';

import { IBasicNode } from '@interfaces/components/tree';
import { IValidationRuleDTO } from '@interfaces/networkElementConfigurations/dto/networkElementConfigurationsDTO';
import { ITagStack } from '@components/common/FlatXMLTree/interface';

import { ETreeTypeNode } from '@enums/tree';

import { FLAT_NODE_PATH_SEPARATOR } from '@constants/common';
import { TAG_OFFSET, XML_NODE_VIRTUALIZED_DEFAULT_HEIGHT as virtualizationHeight } from './constants';

import FlatTreeXMLSearch from './FlatTreeXMLSearch/index';
import { TFlatTreeGetValidation } from '../FlatTree/interface';
import FlatTreeXMLWrapper from './FlatTreeXMLWrapper';
import {
	addClosingMapTreeTags,
	addLastMapTreeChild,
	updateNodeTagsAfterDeletion,
	updateTagsForNonCommonNode,
} from './helpers';
import {
	IAddXMLNode,
	IFlatXMLTreeError,
	IFlatXMLTreeNode,
	TMapTree,
} from './interface';
import {
	getLastChild,
	insertMapItemAfterIndex,
	toggleFlatXMLTree,
} from './utils';

import styles from './FlatTreeXML.module.scss';

enableMapSet();

export type TFlatTreeSetData = React.Dispatch<React.SetStateAction<TMapTree>>;

export interface IFlatTreeProps {
	data: TMapTree;
	setData?: TFlatTreeSetData;
	setChangedNodeIds?: React.Dispatch<React.SetStateAction<string[]>>;
	getValidationRules?: TFlatTreeGetValidation;
	getValidationUpdateString?: (id: string) => Promise<IValidationRuleDTO | undefined>;
	getCopiedNode?: (id: string) => string;
	isReadOnly?: boolean;
	isWithToggle?: boolean;
	isWithLineNumbers?: boolean;
	isWithSkip?: boolean;
	wrapperClassName?: string;
	isLoading?: boolean;
	isWithAdd?: boolean;
	isWithCopy?: boolean;
	withSearch?: boolean;
	errors?: Map<string, IFlatXMLTreeError>;
}

const FlatTreeXML = ({
	data,
	isReadOnly = false,
	wrapperClassName,
	isLoading,
	isWithAdd = false,
	isWithSkip = false,
	isWithCopy = false,
	isWithLineNumbers = true,
	isWithToggle = true,
	setData = () => {},
	getValidationRules,
	setChangedNodeIds,
	getValidationUpdateString,
	getCopiedNode,
	withSearch = true,
	errors,
}: IFlatTreeProps) => {
	const [foundIndexes, setFoundIndexes] = useState<string[]>([]);
	const [searchValue, setSearchValue] = useState('');
	const values = useMemo(() => Array.from(data.values()), [data]);
	const keys = useMemo(() => Array.from(data.keys()), [data]);

	const filteredData = useMemo(() => {
		return values.filter(node => !node.isCollapsed);
	}, [values]);

	const listRef = useRef<List<IFlatXMLTreeNode[]>>(null);

	const copyNodeHandler = useCallback((id: string) => {
		if (!getCopiedNode) {
			return;
		}

		void navigator.clipboard.writeText(getCopiedNode(id));
	}, [getCopiedNode]);

	const skipNodeHandler = useCallback((id: string) => {
		setData(
			produce((draftState) => {
				const node = draftState.get(id)!;

				node.isChecked = !node.isChecked;

				if (setChangedNodeIds) {
					setChangedNodeIds((prev) => {
						return prev.includes(id) ? prev.filter((nodeId) => nodeId !== id) : [...prev, id];
					});
				}
			}),
		);
	}, [setData, setChangedNodeIds]);

	const collapseNodesHandler = useCallback((id: string, parentPath: string) => {
		setData(
			produce((draftState) => {
				toggleFlatXMLTree(draftState, id, parentPath, keys);
			}),
		);
	}, [setData, keys]);

	const revertNodeHandler = useCallback((id: string) => {
		setData(
			produce((draftState) => {
				draftState.get(id)!.value = draftState.get(id)!.defaultValue;

				if (setChangedNodeIds) {
					setChangedNodeIds((prev) => {
						return prev.includes(id) ? prev.filter((nodeId) => nodeId !== id) : prev;
					});
				}
			}),
		);
	}, [setData, setChangedNodeIds]);

	const removeNodeHandler = useCallback((id: string) => {
		setData(
			produce((draftState) => {
				const nodeToDelete = draftState.get(id);
				const parentNode = nodeToDelete?.parentPath
					? draftState.get(nodeToDelete.parentPath.split(FLAT_NODE_PATH_SEPARATOR).pop() || '')
					: undefined;
				let lastDeletedElement: IFlatXMLTreeNode | undefined;
				const originalLastChild = parentNode?.lastChild || undefined;

				if (nodeToDelete?.nodeType && nodeToDelete.lastChild) {
					const startIndex = keys.findIndex((key) => key === id);

					for (let i = startIndex; i < values.length; i++) {
						const value = values[i];
						const key = keys[i];

						if (isCommonNodeRoot(value, nodeToDelete.path)) {
							lastDeletedElement = value;
							draftState.delete(key);
						} else {
							if (parentNode?.lastChild?.id === lastDeletedElement?.id
								|| parentNode?.id == parentNode?.lastChild?.id
								|| isCommonNodeRoot(value, parentNode!.path)
							) {
								updateTagsForNonCommonNode({
									draftState,
									keys,
									startIndex,
									nodeToDelete,
									parentNode,
									value,
									lastDeletedElement,
								});

								break;
							}
						}
					}
				} else {
					const parentLastChild = parentNode?.lastChild?.tags;
					lastDeletedElement = draftState.get(id);
					draftState.delete(id);

					const lastChild = getLastChild(
						draftState,
						nodeToDelete!.parentPath,
						keys.indexOf(parentNode!.id),
						keys,
					);

					if (parentNode?.id && lastChild?.id === parentNode?.id || lastChild?.isEmptyObject) {
						const updatedParent = {
							...parentNode!,
							isEmptyObject: lastChild?.id === parentNode?.id,
							tags:
							lastChild && lastChild?.id !== parentNode?.lastChild?.id && (lastChild?.isEmptyObject)
								? [
									...(parentNode?.lastChild?.tags.filter(tag => {
										if (lastChild?.id === parentNode?.id) {
											return tag.id !== parentNode!.id;
										}

										return tag.depth <= parentNode!.depth;

									}) || []),
								]
								: [...parentLastChild?.filter(tag => tag.id !== parentNode!.id) || []],
						};
						draftState.set(parentNode!.id, updatedParent);
					}
				}

				if (setChangedNodeIds) {
					setChangedNodeIds((prev) => prev.includes(id) ? prev : [...prev, id]);
				}

				updateNodeTagsAfterDeletion({
					draftState,
					parentNode,
					nodeToDelete,
					keys,
					lastDeletedElement,
					originalLastChild,
				});

				addLastMapTreeChild(draftState, keys);

				return draftState;
			}),
		);
	}, [setData, setChangedNodeIds, keys, values]);

	const addNodeHandler = useCallback(<T extends IBasicNode = IFlatXMLTreeNode>({
		parent,
		value,
		key,
		nodeType = undefined,
	}: IAddXMLNode<T>) => {
		setData(
			produce((draftState) => {
				const uniqueKey = uuid() + random();
				const parentNode = draftState.get(parent.id)!;

				const isEmptyObject = nodeType === ETreeTypeNode.object;
				const isParentEmpty = parent.path === parentNode?.lastChild!.path;

				const newNode: IFlatXMLTreeNode = {
					key: key!,
					path: createFlatTreePath(uniqueKey, parent.path),
					depth: parent.depth + 1,
					keysPath: createFlatTreePath(key!, parent.keysPath),
					nodeType,
					defaultValue: undefined,
					parentPath: parent?.path || '',
					lineNumber: parent?.lineNumber || 0,
					description: parent.description || '',
					isCollapsed: Boolean(parent.isHiddenChildren),
					isHiddenChildren: false,
					isEmptyObject,
					lastChild: null,
					tags: [],
					value,
					isNonEditable: false,
					id: uniqueKey,
					isChecked: false,
					isObjectAdded: true,
				};

				const entries = Array.from(draftState.values());

				const listOfParentEmptyObjects = entries.filter((item) => {
					return item.isEmptyObject && isChildNode(item, parent.path);
				});

				if (setChangedNodeIds) {
					setChangedNodeIds((prev) => [...prev, uniqueKey]);
				}

				const mapWithNewNode = insertMapItemAfterIndex(
					draftState,
					parent.id,
					uniqueKey,
					newNode,
				);

				if (isParentEmpty) {
					mapWithNewNode.set(parent.id, {
						...mapWithNewNode.get(parent.id)!,
						lastChild: mapWithNewNode.get(uniqueKey)!,
					});
				}
				const newChild = mapWithNewNode.get(uniqueKey)!;

				const lastTagLineNumber = newChild.tags.at(-1)?.lineNumber || 0;
				const parentTags = {
					...parentNode,
					id: uniqueKey,
					tags: parentNode.tags.map((tag) => ({
						...tag,
						lineNumber: lastTagLineNumber && tag.lineNumber
							? lastTagLineNumber + 1
							: tag.lineNumber,
					})),
				};

				let currentLineNumber = 1;
				const stack: ITagStack[] = [];

				const newKeys = Array.from(mapWithNewNode).map(([key]) => key);

				for (let index = 0; index < newKeys.length; index++) {
					currentLineNumber = addClosingMapTreeTags(
						mapWithNewNode,
						stack,
						newKeys[index],
						newKeys,
						currentLineNumber,
						index,
						[...listOfParentEmptyObjects, parentTags],
					) || 0;
				}

				addLastMapTreeChild(mapWithNewNode, newKeys);

				return mapWithNewNode;
			}),
		);
	}, [setData, setChangedNodeIds]);

	const getChildrenNamesList = useCallback((node: IBasicNode) => {
		const regex = new RegExp('^' + node.keysPath + '/[^/]+$');

		return Array.from(data)
			.filter(([_, item]) => item.keysPath && regex.test(item.keysPath))
			.map(([_, item]) => item.key);
	}, [data]);

	const editNodeHandler = useCallback((id: string, value: string) => {
		setData(
			produce((draftState) => {
				draftState.get(id)!.value = value;

				if (setChangedNodeIds) {
					setChangedNodeIds((prev) => prev.includes(id) ? prev : [...prev, id]);
				}
			}),
		);
	}, [setData, setChangedNodeIds]);

	const findMatchingNodeIndexes = useCallback((search: string) => {
		if (!search) return [];

		const searchRegex = new RegExp(search, 'i');

		return values.reduce<string[]>((acc, node) => {
			if (searchRegex.test(node.value || '') || searchRegex.test(node.key)) {
				acc.push(node.id);
			}

			return acc;
		}, []);
	}, [values]);

	useEffect(() => {
		if (listRef.current) {
			listRef.current.resetAfterIndex(0);
		}

		setSearchValue('');
		setFoundIndexes([]);
	}, [data]);

	const unfoldParentNodes = useCallback((index: string) => {
		setData(
			produce((draftState) => {
				const childNode = draftState.get(index)!;
				const parents = childNode.path.split(FLAT_NODE_PATH_SEPARATOR);

				for (let i = 0; i < parents.length; i++) {
					const parentNode = draftState.get(parents[i])!;

					if (parentNode.isHiddenChildren) {
						toggleFlatXMLTree(draftState, parentNode.id, parentNode.path!, keys, true);
					}
				}

				if (listRef?.current) {
					const nonCollapsedItems = Array
						.from(draftState.values())
						.filter((item) => !item.isCollapsed);

					listRef.current.scrollToItem(nonCollapsedItems.findIndex((item) => item.id === index));
				}
			}),
		);
	}, [setData, keys]);

	const onSearch = useCallback((val: string) => {
		const foundNodes = findMatchingNodeIndexes(val);

		setFoundIndexes(foundNodes);

		if (foundNodes?.length) {
			unfoldParentNodes(foundNodes[0]);
		}
	}, [unfoldParentNodes, findMatchingNodeIndexes]);

	const scrollToIndex = (index: number) => {
		if (listRef?.current && foundIndexes.length) {
			const item = foundIndexes[index];

			unfoldParentNodes(item);
		}
	};

	return (
		<div className={cn(styles.treeWrapper, wrapperClassName)}>
			{withSearch && !!filteredData.length && (
				<FlatTreeXMLSearch
					found={foundIndexes}
					jumpTo={scrollToIndex}
					onSearch={onSearch}
					searchValue={searchValue}
					setSearchValue={setSearchValue}
				/>
			)}
			<div className={styles.treeFrame}>
				{isLoading && <SpinnerOverlay />}
				{(
					<AutoSizer>
						{({ height = 0, width = 0 }) => {
							return (
								<List
									ref={listRef}
									height={height}
									itemCount={filteredData.length}
									itemData={filteredData}
									itemSize={(index: number) => {
										const countOfTags = filteredData[index].tags?.length;

										return countOfTags
											? virtualizationHeight * countOfTags + TAG_OFFSET
											: virtualizationHeight;
									}}
									width={width}
								>
									{({ data, style, index }: any) => {
										return (
											<FlatTreeXMLWrapper
												key={`node_${data[index].id}_${index}`}
												errors={errors}
												getChildrenNamesList={getChildrenNamesList}
												getValidationRules={getValidationRules}
												getValidationUpdateString={getValidationUpdateString}
												index={data[index].id}
												isReadOnly={isReadOnly}
												isWithAdd={isWithAdd}
												isWithCopy={isWithCopy}
												isWithLineNumbers={isWithLineNumbers}
												isWithSkip={isWithSkip}
												isWithToggle={isWithToggle}
												node={data[index]}
												onAdd={addNodeHandler}
												onCollapse={collapseNodesHandler}
												onCopy={copyNodeHandler}
												onEdit={editNodeHandler}
												onRemove={removeNodeHandler}
												onRevert={revertNodeHandler}
												onSkip={skipNodeHandler}
												searchValue={searchValue}
												style={style}
											/>
										);
									}}
								</List>
							);
						}}
					</AutoSizer>)}
			</div>
		</div>
	);
};

export default memo(FlatTreeXML);

